import { Box } from '@material-ui/core';
import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { IProjectPreviewDialogProps, ProjectPreviewDialog } from '..';
import {
	AppState,
	AppThunkDispatch,
	Comparison,
	fetchBosatlas,
	Group,
	MultiShareActions,
	multiShareCloseDialog,
	multiShareConfirm,
	multiShareDeselectComparison,
	multiShareDeselectProject,
	multiShareSelectComparison,
	multiShareSelectProject,
	MultiShareStates,
	Project,
	Student
} from '../../../redux';
import {
	FullscreenDialogHeader,
	FullscreenDialogFooter,
	BosatlasMapSelector,
	DialogWindow,
	MultiShareOverview,
	MultiShareStudentSelector,
	ProgressStepper,
	SelectorOverviewPopup
} from '../../molecules';

import './multi-share-dialog.styles.scss';

export interface IMultiShareDialogStateProps {
	isOpen: boolean;
	selectedProjects: Project[];
	selectedComparisons: Comparison[];
	selectedGroups: Group[];
	originalGroups: Group[];
	fetchBosatlas: () => void;
	selectProject: (project: Project) => void;
	deselectProject: (project: Project) => void;
	selectComparison: (comparison: Comparison) => void;
	deselectComparison: (comparison: Comparison) => void;
	onClose: () => void;
	onConfirm: (
		groups: Group[],
		students: Student[],
		projects: Project[],
		comparisons: Comparison[]
	) => void;
}

export function MultiShareDialogView(
	props: IMultiShareDialogStateProps
): ReactElement {
	const {
		isOpen,
		selectedProjects,
		selectedComparisons,
		selectedGroups,
		originalGroups,
		fetchBosatlas,
		selectProject,
		deselectProject,
		selectComparison,
		deselectComparison,
		onClose,
		onConfirm
	} = props;

	const { t } = useTranslation(['multiShareDialogComponent', 'general']);

	const [shareState, setShareState] = useState<MultiShareStates>(
		MultiShareStates.BosatlasSelector
	);
	const [showSelectionOverview, setShowSelectionOverview] =
		useState<boolean>(false);

	const [projectPreviewState, setProjectPreviewState] =
		useState<IProjectPreviewDialogProps>({
			open: false,
			project: undefined,
			onClose: onClosePreviewDialog
		});

	const selectionCount: number = useMemo<number>(() => {
		return selectedProjects.length + selectedComparisons.length;
	}, [selectedProjects, selectedComparisons]);

	const activeStep: number = useMemo<number>(() => {
		switch (shareState) {
			case MultiShareStates.BosatlasSelector:
				return 0;
			case MultiShareStates.StudentSelector:
				return 1;
			case MultiShareStates.Overview:
				return 2;
			default:
				return 0;
		}
	}, [shareState]);

	const confirmDisabled: boolean = useMemo<boolean>(() => {
		switch (activeStep) {
			case 0:
				return selectionCount === 0;
			case 1:
				return selectedGroups.length === 0;
			default:
				return false;
		}
	}, [activeStep, selectedProjects, selectedComparisons, selectedGroups]);

	function onClosePreviewDialog(): void {
		setProjectPreviewState({ ...projectPreviewState, open: false });
	}

	function onShowPreview(project: Project): void {
		setProjectPreviewState({
			...projectPreviewState,
			open: true,
			project: project
		});
	}

	function setStep(step: number): void {
		switch (step) {
			case 0:
				setShareState(MultiShareStates.BosatlasSelector);
				break;
			case 1:
				setShareState(MultiShareStates.StudentSelector);
				break;
			default:
				break;
		}
	}

	function confirmStep(): void {
		switch (activeStep) {
			case 0: {
				setShareState(MultiShareStates.StudentSelector);
				break;
			}
			case 1: {
				setShareState(MultiShareStates.Overview);
				break;
			}
			case 2: {
				share();
				break;
			}
		}
	}

	function goBack(): void {
		switch (activeStep) {
			case 1:
				setShareState(MultiShareStates.BosatlasSelector);
				break;
			case 2:
				setShareState(MultiShareStates.StudentSelector);
				break;
			default:
				return;
		}
	}

	function share(): void {
		const groups: Group[] = selectedGroups.filter((g: Group) => {
			const index: number = originalGroups.findIndex(
				(og: Group) => og.id === g.id
			);
			return g.students.length === originalGroups[index].students.length;
		});
		const students: Student[] = selectedGroups
			.filter((g: Group) => !groups.includes(g))
			.flatMap((g: Group) => g.students);
		onConfirm(groups, students, selectedProjects, selectedComparisons);
		onClose();
	}

	function onProjectClicked(project: Project): void {
		const selected: boolean = selectedProjects.some(
			(selectedProject: Project) => {
				return selectedProject.id === project.id;
			}
		);

		selected ? deselectProject(project) : selectProject(project);
	}

	function onComparisonClicked(comparison: Comparison): void {
		const selected: boolean = selectedComparisons.some(
			(selectedComparison: Comparison) => {
				return selectedComparison.id === comparison.id;
			}
		);

		selected
			? deselectComparison(comparison)
			: selectComparison(comparison);
	}

	function toggleSelectionOverview(): void {
		setShowSelectionOverview((prev) => !prev);
	}

	return (
		<DialogWindow fullScreen className="multi-share-dialog" open={isOpen}>
			<FullscreenDialogHeader
				onClose={onClose}
				title={t('multiShareDialogComponent:shareMaps')}
			/>
			<ProgressStepper
				activeStep={activeStep}
				labels={[
					t('multiShareDialogComponent:stepper.chooseMaps'),
					t('multiShareDialogComponent:stepper.addStudents'),
					t('multiShareDialogComponent:stepper.finish')
				]}
			/>
			<Box className="multi-share-dialog-content">
				{shareState === MultiShareStates.BosatlasSelector && (
					<BosatlasMapSelector
						onProjectClicked={onProjectClicked}
						projectSelection={selectedProjects}
						comparisonSelection={selectedComparisons}
						onComparisonClicked={onComparisonClicked}
						onShowPreview={onShowPreview}
						sharing
					/>
				)}
				{shareState === MultiShareStates.StudentSelector && (
					<MultiShareStudentSelector />
				)}
				{shareState === MultiShareStates.Overview && (
					<MultiShareOverview setStep={setStep} />
				)}
			</Box>
			<FullscreenDialogFooter
				activeStep={activeStep}
				onConfirmStep={confirmStep}
				onBackClicked={goBack}
				selectorProps={{
					count: selectedProjects.length + selectedComparisons.length,
					text: t('multiShareDialogComponent:selected'),
					selectorOpen: showSelectionOverview,
					onClick: toggleSelectionOverview
				}}
				confirmDisabled={confirmDisabled}
				confirmText={
					activeStep === 2
						? t('multiShareDialogComponent:share')
						: t('general:nextStep')
				}
			/>
			<SelectorOverviewPopup
				title={t('multiShareDialogComponent:chosenMapsShare')}
				subtitle={t('multiShareDialogComponent:amountChosen', {
					amount: selectedProjects.length
				})}
				open={showSelectionOverview}
				selectedProjects={selectedProjects}
				selectedComparisons={selectedComparisons}
				onClose={() => toggleSelectionOverview()}
				fetchBosatlas={fetchBosatlas}
			/>
			{projectPreviewState.project && (
				<ProjectPreviewDialog {...projectPreviewState} />
			)}
		</DialogWindow>
	);
}

function mapStateToProps(state: AppState) {
	return {
		isOpen: state.multiShare.dialogOpen,
		selectedProjects: state.multiShare.selectedProjects,
		selectedComparisons: state.multiShare.selectedComparisons,
		selectedGroups: state.multiShare.selectedGroups,
		originalGroups: state.multiShare.groups
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MultiShareActions>) {
	return {
		fetchBosatlas: () => dispatch(fetchBosatlas()),
		selectProject: (project: Project) =>
			dispatch(multiShareSelectProject(project)),
		deselectProject: (project: Project) =>
			dispatch(multiShareDeselectProject(project)),
		selectComparison: (comparison: Comparison) =>
			dispatch(multiShareSelectComparison(comparison)),
		deselectComparison: (comparison: Comparison) =>
			dispatch(multiShareDeselectComparison(comparison)),
		onClose: () => dispatch(multiShareCloseDialog()),
		onConfirm: (
			groups: Group[],
			students: Student[],
			projects: Project[],
			comparisons: Comparison[]
		) =>
			dispatch(multiShareConfirm(groups, students, projects, comparisons))
	};
}

export const MultiShareDialog = connect(
	mapStateToProps,
	mapDispatchToProps
)(MultiShareDialogView);
